import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Dialog } from '../../../../Components/MUIs';
import dayjs from 'dayjs';
import './SER_BASIC_FormPopup.styles.css';

const STATUS_META = {
  DRAFT: {
    label: '작성중',
    className: 'ser-form-popup__badge--draft',
    icon: 'edit_note',
    cardClass: 'is-draft',
  },
  ACTIVE: {
    label: '현재 적용',
    className: 'ser-form-popup__badge--active',
    icon: 'check_circle',
    cardClass: 'is-active',
  },
  ARCHIVED: {
    label: '보관',
    className: 'ser-form-popup__badge--archived',
    icon: 'inventory_2',
    cardClass: '',
  },
};

/** 중대재해 평가 양식 버전 관리 팝업 */
function SER_BASIC_FormPopup({
  open = false,
  onClose = () => {},
  rows = [],
  loading = false,
  onSelect = () => {},
  onAddNew = () => {},
}) {
  const title = '평가 양식 관리';

  const FormatDate = (value) => {
    if (!value) return '-';
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD') : String(value).slice(0, 10);
  };

  const RenderStatusBadge = (row) => {
    const status = String(row.status || '').toUpperCase();
    const meta = STATUS_META[status] || {
      label: status || '-',
      className: 'ser-form-popup__badge--archived',
      icon: 'help',
    };
    return (
      <span className={`ser-form-popup__badge ${meta.className}`}>
        <span className="material-symbols-outlined">{meta.icon}</span>
        {meta.label}
      </span>
    );
  };

  const GetCardClass = (row) => {
    const status = String(row.status || '').toUpperCase();
    return STATUS_META[status]?.cardClass || '';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="sm"
      fullWidth
      useSaveButton={false}
      closeButtonName="닫기"
      titleSx={{ pb: 2, fontWeight: 700 }}
      contentSx={{ pt: 3, px: 2.5, pb: 2.5 }}
      content={
        <Box className="ser-form-popup" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div className="ser-form-popup__intro">
            <div className="ser-form-popup__intro-icon">
              <span className="material-symbols-outlined">description</span>
            </div>
            <div>
              <div className="ser-form-popup__intro-title">평가에 사용할 양식을 관리합니다</div>
              <p className="ser-form-popup__intro-desc">
                작성 중인 양식을 선택해 편집하고, 완료되면 「현재 버전을 적용하기」로
                평가에 사용할 양식을 지정할 수 있습니다.
              </p>
            </div>
          </div>

          <div className="ser-form-popup__toolbar">
            <span className="ser-form-popup__count">
              등록 양식 <strong>{rows.length}</strong>건
            </span>
            <button type="button" className="ser-form-popup__add-btn" onClick={onAddNew}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                add_circle
              </span>
              신규 양식 추가
            </button>
          </div>

          <div className="ser-form-popup__list-wrap">
            {loading ? (
              <div className="ser-form-popup__loading">
                <CircularProgress size={28} sx={{ color: '#2CA900' }} />
                <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>
                  양식 목록을 불러오는 중...
                </span>
              </div>
            ) : rows.length === 0 ? (
              <div className="ser-form-popup__empty">
                <div className="ser-form-popup__empty-icon">
                  <span className="material-symbols-outlined" style={{ fontSize: 28 }}>
                    folder_open
                  </span>
                </div>
                <div className="ser-form-popup__empty-title">등록된 양식이 없습니다</div>
                <p className="ser-form-popup__empty-desc">
                  「신규 양식 추가」로 첫 평가 양식을 만들거나, 공통 양식으로
                  시작할 수 있습니다.
                </p>
              </div>
            ) : (
              <div className="ser-form-popup__list">
                {rows.map((row) => {
                  const key = `${row.versionId || row.formCode}-${row.versionNo || row.formVer}`;
                  const name = row.versionNm || row.formName || '이름 없음';
                  const ver = row.versionNo || row.formVer;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`ser-form-popup__card ${GetCardClass(row)}`}
                      onClick={() => onSelect(row)}
                    >
                      <div className="ser-form-popup__card-top">
                        <div className="ser-form-popup__card-title">{name}</div>
                        <div className="ser-form-popup__card-badges">
                          {ver ? (
                            <span className="ser-form-popup__badge ser-form-popup__badge--ver">
                              v{ver}
                            </span>
                          ) : null}
                          {RenderStatusBadge(row)}
                          {row.publishYn === 'Y' ? (
                            <span className="ser-form-popup__badge ser-form-popup__badge--ready">
                              <span className="material-symbols-outlined">task_alt</span>
                              적용 가능
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div className="ser-form-popup__stats">
                        <span className="ser-form-popup__stat">
                          <span className="material-symbols-outlined">category</span>
                          대분류 {row.categoryCnt ?? 0}
                        </span>
                        <span className="ser-form-popup__stat">
                          <span className="material-symbols-outlined">fact_check</span>
                          평가지표 {row.indicatorCnt ?? 0}
                        </span>
                        <span className="ser-form-popup__stat">
                          <span className="material-symbols-outlined">checklist</span>
                          착안사항 {row.checkpointCnt ?? 0}
                        </span>
                      </div>

                      <div className="ser-form-popup__meta">
                        <span className="ser-form-popup__meta-item">
                          <span className="material-symbols-outlined">schedule</span>
                          수정일 {FormatDate(row.updateDate)}
                        </span>
                        {row.changeMemo ? (
                          <span className="ser-form-popup__meta-item">
                            <span className="material-symbols-outlined">sticky_note_2</span>
                            {row.changeMemo}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </Box>
      }
    />
  );
}

export default SER_BASIC_FormPopup;
